import * as THREE from 'three'
import { Heightfield } from '../terrain/heightfield'

/**
 * Ski tracks cut into the snow.
 *
 * Detail that earns its place twice: it makes the mountain look skied rather
 * than pristine, and because the par ghost lays its own groove as it goes, a
 * player who is keeping up can see the line it took drawn on the snow ahead of
 * them. That is a racing-line guide you have to earn — fall behind and it is
 * out of sight, exactly as it should be.
 *
 * One draw call per skier. The ribbon is a fixed ring of quads; the oldest tail
 * is simply overwritten once the buffer wraps.
 */

const HEIGHT_OFFSET = 0.07

export class TrackRibbon {
  readonly mesh: THREE.Mesh
  private readonly pos: Float32Array
  private readonly geom = new THREE.BufferGeometry()
  private readonly maxQuads: number
  private quads = 0
  private hasPrev = false
  private prevLx = 0
  private prevLy = 0
  private prevLz = 0
  private prevRx = 0
  private prevRy = 0
  private prevRz = 0
  private lastX = 0
  private lastZ = 0

  constructor(
    private readonly hf: Heightfield,
    colour: THREE.ColorRepresentation,
    opacity: number,
    private readonly halfWidth = 0.62,
    maxQuads = 700,
    /** Minimum travel before a new quad is laid, metres. */
    private readonly step = 1.3,
  ) {
    this.maxQuads = maxQuads
    this.pos = new Float32Array(maxQuads * 6 * 3)
    this.geom.setAttribute('position', new THREE.BufferAttribute(this.pos, 3))
    this.geom.setDrawRange(0, 0)
    // Generous bounds: the ribbon spans the whole slope and never needs culling.
    this.geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6)

    const mat = new THREE.MeshBasicMaterial({
      color: colour,
      transparent: true,
      opacity,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
    this.mesh = new THREE.Mesh(this.geom, mat)
    this.mesh.frustumCulled = false
    // Above the terrain, below the spray and the rider.
    this.mesh.renderOrder = 1
  }

  clear(): void {
    this.quads = 0
    this.hasPrev = false
    this.geom.setDrawRange(0, 0)
  }

  /**
   * Extend the track to the skier's current position.
   * `heading` is the direction of travel; the ribbon is laid across it.
   */
  update(x: number, z: number, heading: number, laying: boolean): void {
    if (!laying) {
      // Break the ribbon so an airborne section does not draw a stripe across
      // the gap it flew over.
      this.hasPrev = false
      return
    }
    if (this.hasPrev && Math.hypot(x - this.lastX, z - this.lastZ) < this.step) return

    // Skier's right in the horizontal plane. Forward is (sin h, 0, cos h), so
    // the perpendicular is (cos h, 0, -sin h).
    const rx = Math.cos(heading) * this.halfWidth
    const rz = -Math.sin(heading) * this.halfWidth

    const lx = x - rx
    const lz = z - rz
    const rx2 = x + rx
    const rz2 = z + rz
    const ly = this.hf.sampleHeight(lx, lz) + HEIGHT_OFFSET
    const ry = this.hf.sampleHeight(rx2, rz2) + HEIGHT_OFFSET

    if (this.hasPrev) {
      const i = (this.quads % this.maxQuads) * 18
      const p = this.pos
      // Two triangles bridging the previous cross-section to this one.
      p[i] = this.prevLx; p[i + 1] = this.prevLy; p[i + 2] = this.prevLz
      p[i + 3] = this.prevRx; p[i + 4] = this.prevRy; p[i + 5] = this.prevRz
      p[i + 6] = lx; p[i + 7] = ly; p[i + 8] = lz

      p[i + 9] = this.prevRx; p[i + 10] = this.prevRy; p[i + 11] = this.prevRz
      p[i + 12] = rx2; p[i + 13] = ry; p[i + 14] = rz2
      p[i + 15] = lx; p[i + 16] = ly; p[i + 17] = lz

      this.quads++
      const drawn = Math.min(this.quads, this.maxQuads) * 6
      this.geom.setDrawRange(0, drawn)
      this.geom.attributes.position.needsUpdate = true
    }

    this.prevLx = lx; this.prevLy = ly; this.prevLz = lz
    this.prevRx = rx2; this.prevRy = ry; this.prevRz = rz2
    this.lastX = x
    this.lastZ = z
    this.hasPrev = true
  }

  dispose(): void {
    this.geom.dispose()
    ;(this.mesh.material as THREE.Material).dispose()
  }
}
